import React, { useMemo } from 'react'
import { Button } from '@chakra-ui/react'
import { Box, Text, Flex, SimpleGrid } from '@chakra-ui/react'

import OutlineBox from '~components/shared/wrappers/outline-box'
import { PatientFieldsFragment, PatientInfo } from '~graphql/generated/graphql'
import { usePatientInfo } from '../patient-info-context'
import MatchingTag from '../matching-tag'

interface Props {
    patient: PatientFieldsFragment
    patientInfo: PatientInfo
    setPatient: (patient: PatientFieldsFragment) => void
}

export default function PatientItem({
    patient,
    patientInfo,
    setPatient,
}: Props) {
    const { clinic_species, clinic_breed } = usePatientInfo()

    const matchingName = useMemo(() => {
        return (
            patient?.name?.toLowerCase().trim() ===
            patientInfo?.patient_name?.toLowerCase().trim()
        )
    }, [patient, patientInfo])

    const matchingSpecies = useMemo(() => {
        return patient?.clinic_species?.id === clinic_species?.id
    }, [patient, patientInfo])

    const matchingBreed = useMemo(() => {
        return patient?.clinic_breed?.id === clinic_breed?.id
    }, [patient, patientInfo])

    return (
        <OutlineBox w="full" py={3} px={5}>
            <Flex
                w="full"
                justifyContent="space-between"
                alignItems="center"
                gridGap={4}
            >
                <Box>
                    <Text fontWeight="semibold" mb={-0.5}>
                        {patient.name}
                    </Text>
                    <Text fontSize="sm" mb={-0.5} color="gray.500">
                        {patient.clinic_species?.species}
                        {patient.clinic_breed?.description
                            ? `, ${patient.clinic_breed?.description}`
                            : ''}
                    </Text>
                </Box>
                <SimpleGrid columns={2} spacing={2}>
                    <MatchingTag doesMatch={[matchingName]} attribute="Name" />
                    <MatchingTag
                        doesMatch={[matchingSpecies]}
                        attribute="Species"
                    />
                    <MatchingTag
                        doesMatch={[matchingBreed]}
                        attribute="Breed"
                    />
                </SimpleGrid>
                <Button
                    colorScheme="brand"
                    size="sm"
                    shadow="md"
                    onClick={() => setPatient(patient)}
                >
                    Select
                </Button>
            </Flex>
        </OutlineBox>
    )
}
